import { t } from "i18next";
import * as z from "zod";

import { QR_TYPES } from "@/features/shared/api/enums";

/**
 * Schéma du formulaire de génération d'un QR code marchand
 * (`POST /QrCodes/merchant/{merchantId}/add`). Le montant est saisi en
 * unité MAJEURE (converti en `amountMinor` au submit) et n'est requis que
 * pour un QR dynamique.
 */
export const createQrCodeSchema = () =>
  z
    .object({
      storeId: z
        .string()
        .trim()
        .uuid({ message: t("invalidFieldError") })
        .or(z.literal(""))
        .optional(),
      qrType: z.enum(QR_TYPES, {
        errorMap: () => ({ message: t("requiredFieldError") }),
      }),
      /** Saisi en unité majeure (converti côté submit). */
      amount: z
        .number({ invalid_type_error: t("invalidFieldError") })
        .nonnegative({ message: t("invalidFieldError") })
        .optional(),
      currency: z
        .string()
        .trim()
        .regex(/^[A-Z]{3}$/, { message: t("invalidFieldError") })
        .or(z.literal(""))
        .optional(),
      expiresAt: z.string().trim().or(z.literal("")).optional(),
    })
    .superRefine((values, ctx) => {
      // QR dynamique = montant fixé ; QR statique = pas de montant.
      if (values.qrType === "DYNAMIC" && !(values.amount && values.amount > 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["amount"],
          message: t("requiredFieldError"),
        });
      }
    });

export type QrCodeFormValues = z.infer<ReturnType<typeof createQrCodeSchema>>;

/** Motif de révocation d'un QR (query param `reason`, requis). */
export const createRevokeQrCodeSchema = () =>
  z.object({
    reason: z.string().trim().min(1, { message: t("requiredFieldError") }),
  });

export type RevokeQrCodeFormValues = z.infer<
  ReturnType<typeof createRevokeQrCodeSchema>
>;
